// @ts-nocheck
import {
  DateSelectArg,
  DateSpanApi,
  DatesSetArg,
  EventInput,
} from "@fullcalendar/core";
import FullCalendar from "@fullcalendar/react";
import interactionPlugin from "@fullcalendar/interaction"; // for drag and drop, click, etc.
import resourceTimelinePlugin from "@fullcalendar/resource-timeline"; // needed for dayClick
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { CalendarComponentsWrapper, CalendarWrapper } from "./styles";
import CustomViewProperties from "./components/customViewProperties";
import PaginationProperties from "./components/paginationProperties";
import CustomSearchInput from "./components/customSearchInput";
import EventContent from "./components/eventContent";
import CustomHeader from "./components/customHeader";
import CustomSlotLabelCalendar from "./components/customSlotLabelCalendar";
import dayGridPlugin from "@fullcalendar/daygrid";
import CustomDayCellContent from "./components/customDayCellContent";
import { useGetCalenderData, useGetListingManagement } from "@services";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  CurrentPageStore,
  isManualReservationOpen,
  loaderState,
  NewCalenderPageCount,
  PopupOverlayNewCalender,
  searchByPropertyName,
  selectedProperty,
  switchReservationsState,
} from "@store/index";
import CustomLoading from "../CustomLoading";
import PopupOverlay from "./components/popupOverlay";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import DayHeaderContent from "./components/dayHeaderContent";
import ManualReservation from "../manualReservation_v2";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export default function NewCalendarComponents() {
  /* ------------------------------- local State ------------------------------ */
  const [resourcesPerPage] = useState(8);
  const [currentPage, setCurrentPage] = useRecoilState(CurrentPageStore); // State to track current page
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  const [categoryPropertyIds, setCategoryPropertyIds] = useState<number[]>([]);
  const [isloaderState, setLoaderState] = useRecoilState(loaderState);
  const [isPopupOverlayNewCalender, setPopupOverlayNewCalender] =
    useRecoilState(PopupOverlayNewCalender);
  const pageCount = useRecoilValue(NewCalenderPageCount);
  const [calendarTitle, setCalendarTitle] = useState<string>("");
  const [switchView, setSwitchView] = useState<
    "dayGridMonth" | "resourceTimelineWeek"
  >("resourceTimelineWeek");
  const valueSelectedProperty = useRecoilValue(selectedProperty);
  const searchByPropertyNameValue = useRecoilValue(searchByPropertyName);
  const switchReservationsStateValue = useRecoilValue(switchReservationsState);
  const isManualReservationOpenValue = useRecoilValue(isManualReservationOpen);
  const [firstDay, setFirstDay] = useState<any>(dayjs().day());
  const [initialDate, setInitialDate] = useState<any>(
    dayjs().format("YYYY-MM-DD")
  );
  const [currentMonthDate, setCurrentMonthDate] = useState<string>(
    dayjs().format("YYYY-MM-DD")
  );
  
  // Calculate visible range for rolling 7-day window (today + next 6 days)
  const visibleRange = useMemo(() => {
    if (switchView === "resourceTimelineWeek") {
      const start = dayjs(initialDate);
      const end = start.add(7, 'days');
      return {
        start: start.format('YYYY-MM-DD'),
        end: end.format('YYYY-MM-DD')
      };
    }
    return undefined; // Monthly view uses default
  }, [initialDate, switchView]);
  
  // Calculate the first visible day in the monthly calendar grid
  const monthlyViewStartDate = useMemo(() => {
    if (switchView === "dayGridMonth") {
      // Get the first day of the current month being viewed
      const firstDayOfMonth = dayjs(currentMonthDate).startOf('month');
      const dayOfWeek = firstDayOfMonth.day();
      
      // Calculate how many days to go back to reach the start of the week
      // firstDay is the starting day of the week (0 = Sunday, 1 = Monday, etc.)
      const daysToSubtract = (dayOfWeek - firstDay + 7) % 7;
      const startDate = firstDayOfMonth.subtract(daysToSubtract, 'days');
      return startDate.format('YYYY-MM-DD');
    }
    return null;
  }, [currentMonthDate, firstDay, switchView]);
  
  /* ---------------------------------- API'S --------------------------------- */
  const { data: getListingManagement } = useGetListingManagement();
  const { data: getCalenderData, isLoading } = useGetCalenderData(
    {
      keyword: switchView === "resourceTimelineWeek" ? "week" : undefined,
      page: pageCount,
      filter:
        switchReservationsStateValue === "all"
          ? undefined
          : switchReservationsStateValue,
      // For monthly view, send propertyId instead of search by name
      propertyId:
        switchView === "dayGridMonth" && valueSelectedProperty?.value
          ? valueSelectedProperty.value
          : undefined,
      // For weekly view, keep search by property name
      search:
        switchView === "resourceTimelineWeek" &&
        typeof searchByPropertyNameValue === "string" &&
        selectedCategoryIds.length === 0 // don't send search when category is selected
          ? searchByPropertyNameValue
          : undefined,
      pageSize:
        switchView === "resourceTimelineWeek" ? resourcesPerPage : undefined,
      pageNumber:
        switchView === "resourceTimelineWeek" ? currentPage : undefined,
      // Send propertyIds when a category is selected (weekly view only)
      propertyIds:
        switchView === "resourceTimelineWeek" && categoryPropertyIds.length > 0
          ? categoryPropertyIds.join(",")
          : undefined,
      // Send appropriate date based on view:
      // Weekly: first visible day in timeline (e.g., 2025-10-30 for week starting Oct 30)
      // Monthly: first visible day in calendar grid (e.g., 2025-10-25 when viewing November)
      currentDate: switchView === "dayGridMonth" 
        ? monthlyViewStartDate || dayjs().format("YYYY-MM-DD")
        : visibleRange?.start || dayjs().format("YYYY-MM-DD"),
    },
    {
      enabled:
        switchView === "dayGridMonth" && valueSelectedProperty?.value
          ? true
          : switchView === "resourceTimelineWeek"
            ? true
            : false,
    }
  );
  /* -------------------------------- Functions ------------------------------- */
  const dataList = useMemo(() => {
    if (!getListingManagement?.result || !Array.isArray(getListingManagement.result)) {
      return [];
    }
    const data = getListingManagement.result.map((item) => {
      return {
        value: item?.propertyId,
        label: item?.propertyName ?? '',
        connectedChannels: item?.connectedChannels ?? [],
      };
    });
    return data ?? [];
  }, [getListingManagement]);

  const getChannelBypropertyId = useCallback(
    (propertyId: string) => {
      if (!propertyId) return [];
      if (!dataList || !Array.isArray(dataList)) return [];
      
      const getMypropertyById = dataList.filter(
        (item) => item?.value?.toString() === propertyId
      );
      const channelsWeeklyView = getMypropertyById.flatMap(
        (item) => item?.connectedChannels ?? []
      );
      const defineMyData = channelsWeeklyView.map((item) => item?.channelName ?? '');
      return defineMyData ?? [];
    },
    [dataList]
  );

  const events: EventInput = useMemo(() => {
    // Guard against undefined or null data to prevent crashes
    if (!getCalenderData?.result || !Array.isArray(getCalenderData.result)) {
      return [];
    }
    
    const data = getCalenderData.result.flatMap((item) => item.dates);
    const allData = getCalenderData.result.flatMap((item) => item);
    const eventsList = data?.map((item) => {
      return {
        resourceId: item.propertyId,
        start: item.bookingInfo?.checkIn
          ? item.bookingInfo?.checkIn
          : item.date,
        end: item.bookingInfo?.checkOut
          ? item.bookingInfo?.checkOut
          : item.date,
        display: !item.bookingInfo?.propertyId ? "background" : null,
        extendedProps: item.bookingInfo
          ? {
              isAutoAccess: getCalenderData?.result?.filter(
                (subItem) => subItem.propertyId === item.propertyId
              )[0]?.isAutoAccess,
              date: item.date,
              wheelhouseRecommendationPrice: item.wheelhouseRecommendationPrice,
              isWheelhouseActive: allData
                ? allData.find((property) => property.propertyId === item.propertyId)?.isWheelhouseActive ?? false
                : false,
              isWheelhouseAutomationEnable: allData
                ? allData.find((property) => property.propertyId === item.propertyId)?.isWheelhouseAutomationEnable ?? false
                : false,
              id: item.bookingInfo?.id,
              propertyId: item.propertyId,
              propertyName: item.bookingInfo?.propertyName,
              channelName: item.bookingInfo?.channelName,
              title: item.bookingInfo?.guestName,
              guestPhoneNumber: item.bookingInfo?.guestPhoneNumber,
              numberOfNights: item.bookingInfo?.numberOfNights,
              totalPrice: item.bookingInfo?.totalPrice,
              price: getChannelBypropertyId(
                item?.propertyId?.toString() ?? ''
              )?.includes("Airbnb")
                ? item.airbnb_daily_price
                : getChannelBypropertyId(item?.propertyId?.toString() ?? '')?.includes(
                      "GatherIn"
                    )
                  ? item.gathren_daily_price
                  : item.msool_daily_price,
              bookingStatus: item.bookingInfo?.bookingStatus,
              checkIn: item.bookingInfo?.checkIn,
              checkOut: item.bookingInfo?.checkOut,
              reservationId: item.bookingInfo?.reservationId,
              availability_type: item?.availability_type,
              isMessageSend: item?.bookingInfo?.isMessageSend,
              isAbleToSendMessage: item?.bookingInfo?.isAbleToSendMessage,
            }
          : {
              isAutoAccess:
                getCalenderData?.result?.filter(
                  (subItem) => subItem.propertyId === item.propertyId
                )[0]?.isAutoAccess ?? false,
              propertyId: item.propertyId,
              date: item.date,
              wheelhouseRecommendationPrice: item.wheelhouseRecommendationPrice,
              isWheelhouseActive: allData
                ? allData.find((property) => property.propertyId === item.propertyId)?.isWheelhouseActive ?? false
                : false,
              isWheelhouseAutomationEnable: allData
                ? allData.find((property) => property.propertyId === item.propertyId)?.isWheelhouseAutomationEnable ?? false
                : false,
              price: getChannelBypropertyId(
                item?.propertyId?.toString() ?? ''
              )?.includes("Airbnb")
                ? item.airbnb_daily_price
                : getChannelBypropertyId(item?.propertyId?.toString() ?? '')?.includes(
                      "GatherIn"
                    )
                  ? item.gathren_daily_price
                  : item.msool_daily_price,

              priceSaturday: getChannelBypropertyId(
                item?.propertyId?.toString() ?? ''
              )?.includes("GatherIn")
                ? item.gathren_daily_price
                : getChannelBypropertyId(item?.propertyId?.toString() ?? '')?.includes(
                      "Airbnb"
                    )
                  ? item.airbnb_daily_price
                  : item.msool_daily_price,
            },
      };
    });

    // ✅ DEDUPLICATION: Remove duplicate reservations based on reservationId
    const uniqueEvents = eventsList?.filter((event, index, self) => {
      // If it's a reservation (has bookingInfo), check for duplicates by reservationId
      if (event.extendedProps?.reservationId) {
        return index === self.findIndex(e => 
          e.extendedProps?.reservationId === event.extendedProps?.reservationId &&
          e.resourceId === event.resourceId
        );
      }
      // For non-reservation events (available/busy dates), keep all
      return true;
    });

    return uniqueEvents ?? [];
  }, [getCalenderData, valueSelectedProperty]);

  const disabledDates = useMemo(() => {
    if (!getCalenderData?.result || !Array.isArray(getCalenderData.result)) {
      return [];
    }
    const data = getCalenderData.result.flatMap((item) => item.dates);
    const getDataFilter = data?.filter(
      (item) =>
        item.availability === "unavailable" && 
        (
          item.availability_type === "busy" || 
          (item.availability_type === "reservation" && item.bookingInfo === null)
        )
    );
    const daysUnavailable = getDataFilter?.map((item) => item.date);
    return daysUnavailable ?? [];
  }, [getCalenderData]);

  const resources = useMemo(() => {
    if (!getCalenderData?.result || !Array.isArray(getCalenderData.result)) {
      return [];
    }
    const data = getCalenderData.result.map((item) => {
      return {
        id: item.propertyId?.toString() ?? '',
        extendedProps: {
          id: item.propertyId?.toString() ?? '',
          title: item.propertyName ?? '',
          image: item.propertyImage ?? '',
        },
      };
    });
    return data ?? [];
  }, [getCalenderData]);
  /* -------------------------------- Functions ------------------------------- */

  /* --------------------------------- hotfix --------------------------------- */
  const disabledDatesHotfix = useMemo(() => {
    if (!getCalenderData?.result || !Array.isArray(getCalenderData.result)) {
      return [];
    }
    const data = getCalenderData.result.flatMap((item) => item.dates);
    const getDataFilter = data?.filter(
      (item) =>
        item.availability === "unavailable" && 
        (
          item.availability_type === "busy" || 
          (item.availability_type === "reservation" && item.bookingInfo === null)
        )
    );
    const daysUnavailable = getDataFilter?.map((item) => {
      return { date: item?.date?.toString(), id: item?.propertyId?.toString() };
    });
    return daysUnavailable ?? [];
  }, [getCalenderData]);

  const isBlockDay = (theDate: string, theId: string) => {
    const validateDate = disabledDatesHotfix.some(
      (item) => item?.date === theDate && item?.id === theId
    );
    return validateDate;
  };
  /* --------------------------------- hotfix --------------------------------- */
  function handleDateSelect(selectInfo: DateSelectArg) {
    const selectedId = selectInfo.resource?.extendedProps.id;
    const filterDataWeeklyView = events.filter(
      (item: any) =>
        dayjs(item.start).day() === 6 &&
        selectedId === item.resourceId.toString()
    );
    const filterDataWeeklyMonthlyView = events.filter(
      (item: any) => dayjs(item.start).day() === 6
    );
    const filteredDaysMonthlyView =
      filterDataWeeklyMonthlyView[filterDataWeeklyMonthlyView.length - 1];
    const filteredDaysWeeklyView = filterDataWeeklyView;

    const weeklyView = events.filter((event: any) => {
      const eventDate = dayjs(event.start);
      return (
        eventDate?.isSameOrAfter(
          dayjs(selectInfo.start).format("YYYY-MM-DD")
        ) &&
        eventDate?.isSameOrBefore(
          dayjs(selectInfo.end).subtract(1, "day").format("YYYY-MM-DD")
        ) &&
        selectedId === event.resourceId.toString()
      );
    });
    const monthlyView = events.filter((event: any) => {
      const eventDate = dayjs(event.start);
      return (
        eventDate?.isSameOrAfter(
          dayjs(selectInfo.start).format("YYYY-MM-DD")
        ) &&
        eventDate?.isSameOrBefore(
          dayjs(selectInfo.end).subtract(1, "day").format("YYYY-MM-DD")
        )
      );
    });
    const allEventsBetweenFromAndToDate =
      switchView === "resourceTimelineWeek" ? weeklyView : monthlyView;
    const getAllPrice = allEventsBetweenFromAndToDate.map((item: any) => {
      return item.extendedProps.price;
    });
    const getWheelhouseRecommendationPrice = allEventsBetweenFromAndToDate.map(
      (item: any) => {
        return item?.extendedProps?.wheelhouseRecommendationPrice ?? "";
      }
    );
    const uniq_WheelhouseRecommendationPrice = [
      ...new Set(getWheelhouseRecommendationPrice),
    ];
    const filteredArr_uniq_WheelhouseRecommendationPrice =
      uniq_WheelhouseRecommendationPrice.filter(
        (item) => item !== null && item !== undefined
      );
    const sorteduniq_WheelhouseRecommendationPrice =
      filteredArr_uniq_WheelhouseRecommendationPrice.sort(
        (a: any, b: any) => a - b
      );
    const uniq = [...new Set(getAllPrice)];
    const filteredArr = uniq.filter(
      (item) => item !== null && item !== undefined
    );
    const sortedPriceArr = filteredArr.sort((a: any, b: any) => a - b);
    setPopupOverlayNewCalender({
      isOpen: true,
      openModule: "advancedSetting",
      isWheelhouseActive:
        getCalenderData?.result[0].isWheelhouseActive ?? false,
      wheelhouseRecommendationPrice:
        sorteduniq_WheelhouseRecommendationPrice as number[],
      isWheelhouseAutomationEnable:
        getCalenderData?.result[0].isWheelhouseAutomationEnable ?? false,
      from: dayjs(selectInfo.start),
      to: dayjs(selectInfo.end),
      isAvailable: !selectedId
        ? !disabledDates.includes(
            dayjs(selectInfo.startStr).format("YYYY-MM-DD")
          )
        : !isBlockDay(
            dayjs(selectInfo.startStr).format("YYYY-MM-DD"),
            selectedId
          ),

      price: sortedPriceArr as number[],
      saturdayPrice:
        switchView === "dayGridMonth"
          ? filteredDaysMonthlyView?.extendedProps?.priceSaturday?.toString() ??
            filteredDaysMonthlyView?.extendedProps?.price?.toString()
          : filteredDaysWeeklyView[0]?.extendedProps?.priceSaturday?.toString() ??
            filteredDaysWeeklyView[0]?.extendedProps?.price?.toString(),
      propertyValue:
        switchView === "dayGridMonth"
          ? valueSelectedProperty
          : {
              value: selectInfo?.resource?._resource?.extendedProps?.id,
              label: selectInfo?.resource?._resource?.extendedProps?.title,
              connectedChannels: [],
            },
    });
  }
  const handleDatesSet = useCallback((info: DatesSetArg) => {
    const newTitle = info.view.title;
    setCalendarTitle(newTitle);
    
    if (switchView === "dayGridMonth") {
      const viewDate = dayjs(info.start).add(7, 'days');
      const newMonthDate = viewDate.format('YYYY-MM-DD');
      // Only update if actually changed to prevent duplicate API calls
      setCurrentMonthDate(prev => prev === newMonthDate ? prev : newMonthDate);
    } else if (switchView === "resourceTimelineWeek") {
      const weekStartDate = dayjs(info.start).format('YYYY-MM-DD');
      // Only update if actually changed to prevent duplicate API calls
      setInitialDate((prev: string) => prev === weekStartDate ? prev : weekStartDate);
    }
  }, [switchView]);

  const selectAllow = (selectInfo: DateSpanApi) => {
    const startDate = dayjs(selectInfo.start).format("YYYY-MM-DD") + "T12:00";
    
    // Get propertyId based on view type
    const propertyId = switchView === "dayGridMonth"
      ? valueSelectedProperty?.value  // Monthly: from dropdown
      : selectInfo.resource?.extendedProps.id;  // Weekly: from resource row
    
    // Must have a property selected
    if (!propertyId) {
      return false;
    }
    
    // Check if date is available (not already booked)
    const selectedCellId = events.filter(
      (item: any) =>
        item.extendedProps.checkIn === startDate &&
        item.extendedProps.propertyId.toString() === propertyId.toString()
    );
    const selectedCell = events.filter(
      (item: any) =>
        item.extendedProps.checkIn &&
        item.extendedProps.checkOut &&
        selectedCellId[0]?.resourceId?.toString() === propertyId.toString() &&
        dayjs(startDate).isBetween(
          item.extendedProps.checkIn,
          dayjs(item.extendedProps.checkOut).subtract(1, "day"),
          "day",
          "[]"
        )
    );
    const isDateAvailable = selectedCell.length === 0;
    
    return isDateAvailable;
  };
  /* ---------------------------------- Hooks --------------------------------- */
  const calendarRef = useRef<FullCalendar | null>(null);

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const refreshCalendar = useCallback(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      // Use setTimeout to move the call outside the render cycle
      setTimeout(() => {
        calendarApi.changeView(switchView);
      }, 0);
    }
  }, [switchView]);

  useEffect(() => {
    if (switchView) {
      refreshCalendar();
    }
  }, [switchView, refreshCalendar]);

  useEffect(() => {
    setLoaderState(isLoading);
  }, [isLoading, setLoaderState]);

  useEffect(() => {
    setPopupOverlayNewCalender(undefined);
  }, [setPopupOverlayNewCalender]);

  const isFiltered =
    (typeof searchByPropertyNameValue === "string" && searchByPropertyNameValue !== "") ||
    selectedCategoryIds.length > 0;
  const isEmptyResults =
    switchView === "resourceTimelineWeek" && !isLoading && getCalenderData?.result?.length === 0;
  const isTrulyEmpty = isEmptyResults && !isFiltered;
  const isFilteredEmpty = isEmptyResults && isFiltered;

  return (
    <Fragment>
      <CalendarComponentsWrapper switchView={switchView}>
        {isloaderState && <CustomLoading />}
        {isPopupOverlayNewCalender?.isOpen && <PopupOverlay />}
        {isManualReservationOpenValue && <ManualReservation />}
        <CustomHeader
          calendarRef={calendarRef}
          switchView={switchView}
          setSwitchView={setSwitchView}
          calendarTitle={calendarTitle}
          getCalenderData={getCalenderData}
          selectedCategoryIds={selectedCategoryIds}
          onCategoryChange={(ids) => {
            setSelectedCategoryIds(ids);
            if (ids.length === 0) setCategoryPropertyIds([]);
          }}
          onCategoryPropertyIds={(ids) => setCategoryPropertyIds(ids)}
          hideControls={isTrulyEmpty}
        />
        {isTrulyEmpty ? (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', minHeight: '60vh', gap: 16, padding: '40px 24px',
            }}>
              <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="60" cy="60" r="60" fill="#f3f0ff"/>
                <path d="M38 48a4 4 0 0 1 4-4h36a4 4 0 0 1 4 4v36a4 4 0 0 1-4 4H42a4 4 0 0 1-4-4V48z" fill="#e0d9ff" stroke="#5b3fff" strokeWidth="2"/>
                <rect x="38" y="44" width="44" height="10" rx="2" fill="#5b3fff" opacity="0.15"/>
                <line x1="38" y1="54" x2="82" y2="54" stroke="#5b3fff" strokeWidth="1.5" opacity="0.4"/>
                <rect x="47" y="62" width="10" height="10" rx="2" fill="#5b3fff" opacity="0.25"/>
                <rect x="62" y="62" width="10" height="10" rx="2" fill="#5b3fff" opacity="0.25"/>
                <rect x="47" y="76" width="10" height="6" rx="2" fill="#5b3fff" opacity="0.15"/>
                <circle cx="82" cy="82" r="14" fill="#fff" stroke="#5b3fff" strokeWidth="2"/>
                <line x1="78" y1="82" x2="86" y2="82" stroke="#5b3fff" strokeWidth="2" strokeLinecap="round"/>
                <line x1="82" y1="78" x2="82" y2="86" stroke="#5b3fff" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <div style={{ textAlign: 'center', maxWidth: 340 }}>
                <p style={{ margin: '0 0 8px', fontWeight: 700, fontSize: 18, color: '#1a1a2e' }}>
                  {t("CALENDAR.NO_PROPERTIES_FOUND")}
                </p>
                <p style={{ margin: 0, fontSize: 14, color: '#8d9cae', lineHeight: 1.6 }}>
                  {t("CALENDAR.NO_PROPERTIES_FOUND_DESC")}
                </p>
              </div>
            </div>
        ) : (
        <CalendarWrapper>
          <FullCalendar
            key={`${initialDate}-${switchView}-${visibleRange?.start || ''}`}
            schedulerLicenseKey="0454582308-fcs-1728214716"
            locale={language}
            ref={calendarRef}
            datesSet={handleDatesSet} // Triggered whenever the dates are set (e.g., after navigating)
            timeZone="utc"
            initialDate={initialDate}
            visibleRange={visibleRange}
            firstDay={firstDay}
            direction={language === "ar" ? "rtl" : "ltr"}
            plugins={[resourceTimelinePlugin, interactionPlugin, dayGridPlugin]}
            initialView={switchView} // Displays the week in resource timeline
            resources={resources} // all properties
            events={events}
            resourceAreaWidth={200} // Set a fixed width for the resource column
            slotLabelFormat={{ weekday: "long", day: "numeric" }} // Displays days in the header
            slotLabelContent={(info) => <CustomSlotLabelCalendar info={info} />}
            slotDuration="24:00" // Each slot is 1 day
            height="auto"
            headerToolbar={false} // Hide default header
            dayCellContent={(info) => (
              <CustomDayCellContent info={info} disabledDates={disabledDates} />
            )}
            dayHeaderContent={(info) => <DayHeaderContent info={info} />}
            eventContent={(arg) => (
              <EventContent
                id={arg?.event.extendedProps?.id}
                isAutoAccess={arg?.event?.extendedProps?.isAutoAccess}
                getCalenderData={getCalenderData}
                date={arg.event?.extendedProps?.date}
                display={arg?.event.display as "background" | null}
                cellPrice={arg.event?.extendedProps?.price}
                bookingStatus={
                  arg?.event.extendedProps?.bookingStatus as
                    | "مؤكد"
                    | "new"
                    | "cancelled"
                    | "ملغي"
                }
                title={arg?.event.extendedProps?.title}
                totalPrice={arg?.event.extendedProps?.totalPrice}
                channelName={
                  arg?.event.extendedProps?.channelName as
                    | "Airbnb"
                    | "GatherIn"
                    | "Booking"
                    | "Agoda"
                    | "Msool"
                }
                checkIn={arg.event.extendedProps.checkIn}
                checkOut={arg.event.extendedProps.checkOut}
                guestPhoneNumber={arg.event.extendedProps.guestPhoneNumber}
                propertyId={arg.event.extendedProps.propertyId}
                propertyName={arg.event.extendedProps.propertyName}
                reservationId={arg.event.extendedProps.reservationId}
                availability_type={arg.event.extendedProps.availability_type}
                isMessageSend={arg.event.extendedProps.isMessageSend}
                isAbleToSendMessage={arg.event.extendedProps.isAbleToSendMessage}
                wheelhouseRecommendationPrice={
                  arg.event.extendedProps.wheelhouseRecommendationPrice
                }
                isWheelhouseActive={arg.event.extendedProps.isWheelhouseActive}
                isWheelhouseAutomationEnable={
                  arg.event.extendedProps.isWheelhouseAutomationEnable
                }
                isDirectPaymentAccepted={
                  arg.event.extendedProps?.isDirectPaymentAccepted
                }
              />
            )} //Event Content
            eventColor="transparent" // Global background color for all events
            resourceAreaColumns={[
              {
                headerContent: () => <CustomSearchInput />,
                cellContent: (arg) => <CustomViewProperties arg={arg} />, // Custom view properties
              },
            ]}
            select={handleDateSelect} // Enable selection
            selectable={true}
            selectMirror={true}
            dayMaxEvents={true}
            selectAllow={(selectInfo) => selectAllow(selectInfo)}
            // Touch screen support - reduce long press delay for better touch responsiveness
            longPressDelay={5}
            selectLongPressDelay={5}
          />
          {isFilteredEmpty && (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', padding: '48px 24px', gap: 12,
            }}>
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="32" cy="32" r="32" fill="#f3f0ff"/>
                <circle cx="28" cy="27" r="10" stroke="#5b3fff" strokeWidth="2" fill="none"/>
                <line x1="35" y1="34" x2="44" y2="43" stroke="#5b3fff" strokeWidth="2" strokeLinecap="round"/>
                <line x1="24" y1="27" x2="32" y2="27" stroke="#5b3fff" strokeWidth="2" strokeLinecap="round" opacity="0.4"/>
              </svg>
              <p style={{ margin: 0, fontWeight: 700, fontSize: 16, color: '#1a1a2e' }}>
                {selectedCategoryIds.length > 0
                  ? t("CALENDAR.NO_PROPERTIES_IN_CATEGORY")
                  : t("CALENDAR.NO_PROPERTIES_FOUND")}
              </p>
              <p style={{ margin: 0, fontSize: 13, color: '#8d9cae', lineHeight: 1.6, textAlign: 'center', maxWidth: 300 }}>
                {selectedCategoryIds.length > 0
                  ? t("CALENDAR.NO_PROPERTIES_IN_CATEGORY_DESC")
                  : t("CALENDAR.NO_PROPERTIES_FOUND_DESC")}
              </p>
            </div>
          )}
          {switchView === "resourceTimelineWeek" && !isFilteredEmpty && (
            <PaginationProperties
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              resourcesPerPage={resourcesPerPage}
              lengthOfRecords={getCalenderData?.totalRecords ?? 0}
            />
          )}
        </CalendarWrapper>
          )}
      </CalendarComponentsWrapper>
    </Fragment>
  );
}
